import type { GameId } from '../data/games-catalog';
import { VIRTUAL_REWARD_ECONOMY_ENABLED } from './rewards';

export type BetaSurfaceMode = 'curated' | 'full';

export type BetaHeldFeature =
  | 'ambassador'
  | 'annual-report'
  | 'billing'
  | 'cloud-sync'
  | 'competitions'
  | 'dashboard-modes'
  | 'filings'
  | 'invite-flows'
  | 'jobs'
  | 'map-courses'
  | 'map-expeditions'
  | 'map-journeys'
  | 'map-printables'
  | 'map-puzzle'
  | 'map-travels'
  | 'missions'
  | 'nature-id'
  | 'pricing'
  | 'quests'
  | 'recommendations'
  | 'reports'
  | 'shop'
  | 'skill-map'
  | 'stories'
  | 'weekly-digest'
  | 'wish-list';

const rawSurface = import.meta.env.VITE_BETA_SURFACE;
const internalFullAccess = import.meta.env.VITE_BETA_INTERNAL_FULL_ACCESS === 'true';

export const betaSurfaceMode: BetaSurfaceMode = rawSurface === 'full' ? 'full' : 'curated';
export const isFullBetaSurface = internalFullAccess || betaSurfaceMode === 'full';
export const isCuratedBetaSurface = !isFullBetaSurface;

export const BETA_GAME_IDS: readonly GameId[] = [
  // Words
  'word-jumble',
  'word-constructor',
  'word-drop',
  'typing-challenge',
  'phoneme-blender',
  'sentence-puzzle',
  'reading-coach',
  'spelling-bee',
  'synonym-sprint',
  'word-chains',
  'word-search',
  'word-wizard',
  // Math
  'math-racer',
  'balloon-pop',
  'speed-math',
  'base-10-blocks',
  'fraction-bars',
  'number-line',
  'word-problem-coach',
  'number-blaster',
  // Quizzes
  'science-quiz',
  'history-quiz',
  'park-quest',
  'language-flashcards',
  'world-quiz',
  'name-that-state',
  'name-that-country',
  // Arcade
  'crown-quest',
  'pixel-rally',
  'stack-attack',
  'memory-match',
  'archery',
  'reaction-color',
  'baseball-derby',
  'buzzer-beater',
  'reaction-pattern',
  'snake',
  'soccer',
  // Brain Train
  'maze-runner',
  'fossil-dig',
  // 3D labs
  'dino-viewer',
  'ocean-lab',
  'animal-kingdom-lab',
  'botany-lab',
  'solar-system-lab',
  'spacecraft-lab',
  'mars-mission-lab',
  'construction-lab',
  'transport-lab',
  // Creative
  'story-builder',
  'pattern-studio',
  'sound-match',
  // Wellness
  'weather-tracker',
  'movement-challenge',
  'calm-corner',
];

const BETA_GAME_ID_SET = new Set<GameId>(BETA_GAME_IDS);
const CURATED_BETA_FEATURES = new Set<BetaHeldFeature>([
  'jobs', 'cloud-sync', 'invite-flows',
  // Enabled for ALL users 2026-06-15 — the kid-value features shipped this week.
  // Adversarially verified non-destructive: read-only on render, empty-state-safe
  // for brand-new users, no data/schema writes triggered by viewing, no dead-end
  // nav (disabled routes show the hold page, not a 404).
  'skill-map', 'recommendations', 'missions', 'quests',
  // Enabled 2026-07-11 — beta users can now purchase. With VITE_BETA_GATE_ACTIVE
  // still true, /pricing renders the single $20/mo Launch card (not the public
  // 3-tier page), so the invite-only beta posture is unchanged.
  'pricing',
  // Enabled 2026-09-01 — the outdoor-learning lander now leads with Nature ID,
  // and the authenticated camera flow is already parent-controlled, rate-
  // limited, payload-validated, and private to the family device/account.
  'nature-id',
]);

export function isBetaFeatureEnabled(feature: BetaHeldFeature): boolean {
  if (feature === 'shop' && !VIRTUAL_REWARD_ECONOMY_ENABLED) return false;
  return isFullBetaSurface || CURATED_BETA_FEATURES.has(feature);
}

export function isBetaGameEnabled(gameId: GameId): boolean {
  return isFullBetaSurface || BETA_GAME_ID_SET.has(gameId);
}

export function filterBetaGames<T extends { id: GameId }>(games: readonly T[]): T[] {
  return isFullBetaSurface ? [...games] : games.filter((game) => BETA_GAME_ID_SET.has(game.id));
}
